export type {
  InternalSalesReferenceContent,
  SalesReferenceDetailCard,
  SalesReferenceDetailSection,
  SalesReferenceQuickAnswer,
} from '@neural-intelligence/shared';

import type { InternalSalesReferenceContent } from '@neural-intelligence/shared';

export const internalSalesReferenceContent: InternalSalesReferenceContent = {
  defaultPitch:
    'Neural Intelligence is designed as a controlled AI workflow. Access is authenticated and role-restricted, customers are separated by chatbot scope, widget usage can be limited to approved domains, and retention controls are available for key workflow data. The AI features do process content, so the safest positioning is honest control and visibility limits, not zero-storage or zero-processing claims.',
  useOrder: [
    'Start with the default pitch instead of improvising a security promise.',
    'Use Quick Answers for the first response on a live call.',
    'Open a deep-dive section only if the prospect keeps pushing on that topic.',
    'Escalate legal, compliance, architecture, or special-contract questions instead of guessing.',
  ],
  strongestTalkingPoints: [
    'Access inside the product is authenticated and role-restricted.',
    'Customers are separated by chatbot ownership and assignment rules.',
    'The embeddable widget can be restricted to approved domains.',
    'Retention controls are available for conversations and email draft records.',
    'The email responder is a draft-first workflow, not an unsupervised auto-send tool.',
    'Mailbox OAuth tokens are encrypted at rest.',
    'The platform is designed to limit internal visibility by role rather than giving every user the same view.',
  ],
  escalationPreview: [
    'Compliance or certification status such as HIPAA, SOC 2, ISO 27001, or GDPR.',
    'DPAs, procurement language, or contract-level security commitments.',
    'Detailed encryption architecture, data residency, subprocessors, or vendor-risk reviews.',
    'Incident response, penetration testing, breach notification, or special retention/export promises.',
  ],
  quickAnswers: [
    {
      id: 'safe',
      question: 'Is our data safe with this?',
      shortAnswer:
        'Yes. The product is built around authenticated access, role restrictions, customer separation, domain controls for the widget, and retention controls for key workflow data.',
      ifTheyPush:
        'The safest framing is controlled access and honest handling. The product does process and store the data needed to run its workflows, so avoid promising that nothing is stored or processed.',
      tags: ['security', 'privacy', 'safe', 'trust'],
    },
    {
      id: 'customers',
      question: 'Do customers share data with each other?',
      shortAnswer:
        'No. Customer users are limited to their own assigned chatbot rather than a shared data view.',
      ifTheyPush:
        'Admins have broader management access and sales access is narrower, but customer-facing access is scoped by chatbot ownership and assignment rules.',
      tags: ['tenant', 'separation', 'customers', 'data'],
    },
    {
      id: 'visibility',
      question: 'Who inside your app can see what?',
      shortAnswer:
        'Access is role-based. Admins have broad management access, sales users have narrower access, and customer users are limited to their own chatbot.',
      ifTheyPush:
        'Some higher-sensitivity workflows are even tighter. For example, sales users cannot manage mailbox connections, and email-draft visibility is more limited internally than it is for the customer using that chatbot.',
      tags: ['roles', 'visibility', 'admin', 'sales', 'customer'],
    },
    {
      id: 'storage',
      question: 'Do you store chats or customer content?',
      shortAnswer:
        'Yes. The platform stores the operational data needed to run chatbot, reporting, and email-draft workflows.',
      ifTheyPush:
        'Do not call it ephemeral. Conversations are stored, and the system also stores chatbot configuration, knowledge-base material, and email draft workflow records when those features are used.',
      tags: ['storage', 'conversations', 'content', 'data'],
    },
    {
      id: 'openai',
      question: 'Does OpenAI process the content?',
      shortAnswer:
        'Yes. The AI features process the relevant customer content through OpenAI so the system can generate chat responses and email drafts.',
      ifTheyPush:
        'That can include chat messages, chatbot instructions, FAQs, uploaded knowledge, website-extracted context, incoming emails, and training examples used for drafting.',
      tags: ['openai', 'ai', 'third party', 'processing'],
    },
    {
      id: 'widget',
      question: 'Can the widget be limited to our website?',
      shortAnswer:
        'Yes. The embeddable widget can be configured to run only on approved domains.',
      ifTheyPush:
        'That is one of the strongest customer-control points for website deployments because the widget is not treated as globally open by default.',
      tags: ['widget', 'domains', 'website', 'embed'],
    },
    {
      id: 'email-responder',
      question: 'How should I describe the email responder?',
      shortAnswer:
        'Describe it as a draft-first workflow with human review, not an autonomous sending system.',
      ifTheyPush:
        'The system reads inbound email so it can prepare a draft, but the safety story is still customer control over what actually gets sent.',
      tags: ['email responder', 'draft', 'send', 'mailbox'],
    },
    {
      id: 'mailbox-passwords',
      question: 'Do you store mailbox passwords?',
      shortAnswer:
        'No. Gmail and Outlook use OAuth rather than mailbox passwords, and stored OAuth tokens are encrypted at rest.',
      ifTheyPush:
        'If mailbox OAuth is the sticking point, there is also a forwarding option that avoids storing mailbox OAuth tokens while still letting the system generate drafts.',
      tags: ['oauth', 'passwords', 'tokens', 'mailbox'],
    },
    {
      id: 'retention',
      question: 'Do customers have retention controls?',
      shortAnswer:
        'Yes. Retention controls are available for conversation data and email draft records, with automated cleanup based on the configured window.',
      ifTheyPush:
        'Do not imply every record in the system uses the same automatic retention setting. The safe answer is that key workflow data has retention controls, while other records follow normal product actions.',
      tags: ['retention', 'deletion', 'cleanup', 'data lifecycle'],
    },
    {
      id: 'compliance',
      question: 'What do I say if they ask about HIPAA, SOC 2, GDPR, or contract language?',
      shortAnswer:
        'Do not answer from memory. Escalate that question so the customer gets an approved response.',
      ifTheyPush:
        'The codebase is not proof of certification status or legal commitments, so the right move is escalation rather than an on-the-spot promise.',
      tags: ['compliance', 'legal', 'contract', 'dpa'],
    },
  ],
  detailSections: [
    {
      id: 'email-deep-dive',
      title: 'Email Responder',
      whenToUse: 'Use when the prospect asks about inbox access, mailbox permissions, drafts, or token storage.',
      summary:
        'Keep the positioning simple: draft-first workflow, customer control, and honest disclosure that relevant email content is processed to generate the draft.',
      cards: [
        {
          title: 'Safest positioning',
          summary:
            'This feature is strongest when described as a controlled drafting workflow rather than an autonomous sender.',
          bullets: [
            'It reads inbound email and generates a draft reply for review.',
            'The core safety story is human review before anything is sent.',
            'Do not describe it as an unsupervised auto-send agent.',
          ],
        },
        {
          title: 'Mailbox access and permissions',
          summary:
            'Be accurate about mailbox permissions. The product needs inbox and draft-handling permissions, so never call it read-only access.',
          bullets: [
            'Gmail scopes include gmail.readonly and gmail.compose.',
            'Outlook scopes include User.Read, Mail.Read, Mail.ReadWrite, Mail.Send, openid, email, and offline_access.',
            'The product behavior is still draft-first even though the provider scopes include write/send-related permissions.',
          ],
        },
        {
          title: 'Storage and control',
          summary:
            'OAuth tokens are not stored in plaintext, and customers are not locked into the connection.',
          bullets: [
            'Gmail and Outlook use OAuth rather than mailbox passwords.',
            'Stored OAuth access and refresh tokens are encrypted at rest.',
            'Customers can disconnect the integration, and the system stops using that mailbox connection afterward.',
            'A forwarding option exists for customers who do not want mailbox OAuth token storage.',
          ],
        },
        {
          title: 'What gets stored',
          summary:
            'Do not imply that email data is ephemeral or never stored.',
          bullets: [
            'The workflow stores draft-related records so customers can review and manage generated replies.',
            'Email draft retention controls are available.',
            'Relevant email content is processed by OpenAI to generate the draft.',
          ],
        },
      ],
    },
    {
      id: 'access-visibility',
      title: 'Access and Visibility',
      whenToUse: 'Use when the prospect asks who can see data or whether customers are isolated from each other.',
      summary:
        'The clean answer is role-based access plus customer scoping. Avoid absolutes about nobody ever seeing anything under any circumstance.',
      cards: [
        {
          title: 'Role model',
          summary:
            'Keep the access story short and concrete.',
          bullets: [
            'Admins have broad management access.',
            'Sales users have a narrower set of allowed chatbots and actions.',
            'Customer users are limited to their own assigned chatbot.',
          ],
        },
        {
          title: 'Customer separation',
          summary:
            'Customer-facing access is separated by chatbot scope rather than a shared tenant view.',
          bullets: [
            'Customers do not browse other customer chatbots.',
            'Chatbot ownership and assignment rules control access boundaries.',
            'Use the phrase customer separation or customer scope instead of more technical tenancy language if the buyer does not need it.',
          ],
        },
        {
          title: 'Sensitive workflow restrictions',
          summary:
            'Some workflows are tighter than the base role model and are worth calling out only when relevant.',
          bullets: [
            'Sales users cannot manage mailbox connections.',
            'Standard internal email-draft listing views are more restricted than the customer view for that chatbot.',
            'The platform is designed to limit internal visibility by role, not to provide the same visibility to every internal user.',
          ],
        },
      ],
    },
    {
      id: 'data-handling',
      title: 'Data Handling and Retention',
      whenToUse: 'Use when the prospect asks what the platform stores, how long data lives, or what content powers the AI.',
      summary:
        'The safest path is to be honest: the system stores the operational data needed to run workflows and offers retention controls for key high-volume records.',
      cards: [
        {
          title: 'What the platform stores',
          summary:
            'Avoid vague or defensive wording. Say what the system needs to run.',
          bullets: [
            'Chatbot configuration and instructions.',
            'FAQs and other knowledge-base inputs.',
            'Conversation history used by the product workflow and reporting.',
            'Email-draft workflow data when the responder feature is enabled.',
          ],
        },
        {
          title: 'Retention controls',
          summary:
            'Retention is a real control point, but it does not apply uniformly to every record type.',
          bullets: [
            'Conversation data can use configured retention windows with automated cleanup.',
            'Email draft records can use configured retention windows with automated cleanup.',
            'Do not promise that every record in the system uses the same automatic deletion policy.',
          ],
        },
        {
          title: 'Knowledge-base and website content',
          summary:
            'Knowledge inputs exist so the chatbot can answer from customer material rather than generic model behavior.',
          bullets: [
            'FAQs and instructions are stored in the app.',
            'Uploaded knowledge-base files are used to support AI responses.',
            'Website extraction uses AI processing to turn site content into structured chatbot context.',
          ],
        },
      ],
    },
    {
      id: 'ai-processing',
      title: 'AI and OpenAI Processing',
      whenToUse: 'Use when the prospect asks whether a third party sees the content or whether the product is zero-processing.',
      summary:
        'This section should stay direct. The AI workflow needs relevant customer content to produce answers and drafts.',
      cards: [
        {
          title: 'What OpenAI processes',
          summary:
            'The product is not a zero-processing architecture.',
          bullets: [
            'Chat messages and chatbot instructions for chatbot replies.',
            'FAQs, uploaded knowledge, and website-extracted context when those are part of the answer flow.',
            'Incoming email content and drafting context when the email responder is used.',
          ],
        },
        {
          title: 'How to talk about it',
          summary:
            'Direct language builds more trust than evasive language here.',
          bullets: [
            'Say that OpenAI processes the relevant customer content needed for the AI feature to work.',
            'Do not say that no third party ever processes the content.',
            'Do not say forwarding avoids AI processing of the content itself.',
          ],
        },
      ],
    },
    {
      id: 'account-security',
      title: 'Account Security',
      whenToUse: 'Use when the prospect asks about login protection, sessions, or baseline application security.',
      summary:
        'This is a supporting section, not the main sales story. Keep it grounded in real controls and avoid over-positioning enterprise features that are not present.',
      cards: [
        {
          title: 'Baseline controls',
          summary:
            'Practical access controls are a better story here than buzzwords.',
          bullets: [
            'Accounts use password-based login with hashed passwords.',
            'The app uses authenticated sessions and session expiration.',
            'Login protections, rate limiting, and activity logging are part of the system.',
          ],
        },
        {
          title: 'How to frame it',
          summary:
            'Keep the claim smaller and more defensible than a full enterprise-security promise.',
          bullets: [
            'Say the product includes practical account-security controls and session protections.',
            'Do not promise MFA for all users.',
            'Do not claim the app is enterprise identity complete out of the box.',
          ],
        },
      ],
    },
    {
      id: 'widget-controls',
      title: 'Widget and Domain Controls',
      whenToUse: 'Use when the prospect asks how embedded chatbot access is controlled on their site.',
      summary:
        'This is one of the cleanest control stories in the platform and should stay easy to explain.',
      cards: [
        {
          title: 'Approved-domain controls',
          summary:
            'The widget can be limited to the customer domains that are explicitly configured for that chatbot.',
          bullets: [
            'This gives the customer control over where the widget is allowed to run.',
            'It is a stronger answer than saying anyone can embed it anywhere.',
            'Use this point early for website-focused buyers because it is easy to understand and reassuring.',
          ],
        },
      ],
    },
  ],
  claimsToAvoid: [
    'Your data is completely inaccessible to us.',
    'Nothing is stored.',
    'No third party processes the content.',
    'All customer data is end-to-end encrypted.',
    'We support MFA for all users.',
    'The email responder only has read-only mailbox access.',
    'The system never stores chats or email content.',
    'We are HIPAA, SOC 2, ISO 27001, or GDPR compliant unless that has been separately validated and approved.',
  ],
  whenToEscalate: [
    'Formal compliance or certification questions.',
    'DPAs, MSAs, procurement addenda, or any contract wording request.',
    'Detailed encryption architecture or security-architecture review questions.',
    'Data residency, subprocessors, or vendor risk documentation requests.',
    'Penetration testing, incident response, breach notification, or special security commitments.',
    'Special deletion, export, or retention promises beyond current product behavior.',
  ],
  safeClose:
    'The safest way to position Neural Intelligence is as a controlled AI workflow. Access is authenticated and role-restricted, customers are separated by chatbot scope, the widget can be limited to approved domains, key workflow data has retention controls, and the email responder stays in a draft-first human-review flow. The AI features do process relevant customer content, so we focus on honest handling and customer control rather than blanket claims that nothing is stored or processed.',
};
